import {Component, Inject, OnDestroy} from '@angular/core';
import {FormBuilder, FormGroup, Validators, AbstractControl, AsyncValidatorFn} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Subject, Observable, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, take, map, switchMap, tap, catchError} from 'rxjs/operators';
import {ReferenceLookupValue} from 'src/app/modules/clm/types/reference-lookup-value.enum';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {CustomValidator} from '../../../../common';
import {DisplayStrings} from '../../../../common/constants/DisplayStrings';
import {NameValueCodeDTO} from '../../../../common/models/NameValueCodeDTO';
import {LookupService} from '../../../../common/services/lookup.service';
import {SharedService} from '../../../../common/services/shared-service';
import {ApplicationAddressDTO} from '../../../models/application/ApplicationAddressDTO';
import {ReferenceDTO} from '../../../models/application/ReferenceDTO';
import {ReferenceService} from '../../../services/reference.service';

export function suburbValidator(getReturnedFilteredSuburbs: (formValue: any) => Observable<ReferenceDTO[]>): AsyncValidatorFn {
  return (control: AbstractControl): Observable<{ [key: string]: any } | null> => {
    const value = control.value;

    if (!value) {
      return of(null);
    }

    return of(value).pipe(
      switchMap(val => getReturnedFilteredSuburbs(val).pipe(
        take(1), // Ensures only the first emission is used
        map(suburbs => {
          const found = suburbs.some(
            option => option.value.toLowerCase() === val.toLowerCase()
          );
          return found ? null : {notInList: true};
        })
      ))
    );
  };
}


enum Controls {
  AddressTypeCode = 'addressTypeCode',
  AddressLine1Number = 'addressLine1Number',
  AddressLine1Name = 'addressLine1Name',
  AddressLine2Number = 'addressLine2Number',
  AddressLine2Name = 'addressLine2Name',
  Suburb = 'suburb',
  CountryCode = 'countryCode',
  PostalCode = 'postalCode',
  POBoxNumber = 'poBoxNumber',
}

@Component({
  selector: 'app-application-address-update-dialog',
  templateUrl: './application-address-update-dialog.html',
  styleUrls: [
    '../../../../../../styles/global/_dialog-styles.scss',
  ]
})
export class ApplicationAddressDialogComponent implements OnDestroy {
  readonly controls = Controls;
  readonly minCityTownSearchLength = 3;
  readonly EDIT_ADDRESS_HEADING = 'Edit Address';
  readonly ADD_ADDRESS_HEADING = 'Add Address';
  readonly COUNTRY_CODE = {
    SOUTH_AFRICA: 'ZA',
  };
  protected readonly CustomValidator = CustomValidator;
  protected readonly DisplayStrings = DisplayStrings;
  heading: string;
  addressFormGroup: FormGroup;
  countries: NameValueCodeDTO[] = [];
  filteredSuburbs: ReferenceDTO[] = [];
  addressTypes: ReferenceDTO[] = [];
  private suburbSearchText$: Subject<string> = new Subject<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      address: ApplicationAddressDTO,
      hasPostalAddress?: boolean,
      hasResidentialAddress?: boolean,
      ADDRESS_TYPE?: {
        RESIDENTIAL: string,
        POSTAL: string
      }
    },
    private formBuilder: FormBuilder,
    private lookupService: LookupService,
    public dialogRef: MatDialogRef<ApplicationAddressDialogComponent>,
    private referenceService: ReferenceService,
    private sharedService: SharedService
  ) {
    this.initForm();
    this.getListOfLookupValues(LookupValue.Country);
    if (data?.address) {
      this.setFormValues(data.address);
      this.heading = this.EDIT_ADDRESS_HEADING;
    }
    else {
      this.heading = this.ADD_ADDRESS_HEADING;
      this.addressFormGroup.patchValue({
        [Controls.CountryCode]: this.COUNTRY_CODE.SOUTH_AFRICA, // Default
      });
    }

    this.suburbSearchText$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter(text => text?.length >= this.minCityTownSearchLength)
      )
      .subscribe(searchText => {
        this.searchSuburbs(searchText);
      });
  }


  isResidentialOptionDisabled(residentialType: string): boolean {
    // Will ensure that existing pages using this component is not influenced
    if (!this.data.ADDRESS_TYPE) {
      return false;
    }

    const {RESIDENTIAL, POSTAL} = this.data.ADDRESS_TYPE;
    const {hasPostalAddress, hasResidentialAddress} = this.data;

    switch (residentialType) {
    case RESIDENTIAL:
      if (this.data?.address?.addressTypeCode === RESIDENTIAL) {
        return false;
      }
      return hasResidentialAddress;
    case POSTAL:
      if (this.data?.address?.addressTypeCode === POSTAL) {
        return false;
      }
      return hasPostalAddress;
    default:
      return false;
    }
  }

  ngOnDestroy() {
    this.suburbSearchText$.complete();
  }

  getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity).subscribe(
      (response: NameValueCodeDTO[]) => {
        if (entity === LookupValue.Country) {
          this.countries = response;
        }
      },
    );

    this.referenceService.getAllByFieldName(ReferenceLookupValue.AddressTypeCode)
      .pipe(take(1))
      .subscribe({
        next: (results: ReferenceDTO[]) => {
          this.addressTypes = results;
        },
      });
  }

  onSave(): void {
    if (this.addressFormGroup.valid) {
      const formValue = this.addressFormGroup.value;
      const poBoxNumber = this.addPOBoxPrefix(formValue[Controls.POBoxNumber]);
      const addressTypeCode = formValue[Controls.AddressTypeCode];
      const suburbValue = formValue[Controls.Suburb];
      let cityTownCode = this.data?.address?.cityTownCode;
      let suburb = this.data?.address?.suburb;
      const cityTownReference = this.getCityTownReferenceFromValue(suburbValue);
      if (cityTownReference != null)
      {
        cityTownCode = cityTownReference.key;
        suburb = cityTownReference.value;
      }

      const updatedAddress: ApplicationAddressDTO = {
        ...this.data.address,
        ...formValue,
        poBoxNumber,
        addressTypeCode,
        suburb,
        cityTownCode,
      };

      this.dialogRef.close(updatedAddress);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSuburbSearchInput(event: any) {
    const searchText = event.target.value;
    if (typeof searchText === 'string') {
      this.suburbSearchText$.next(searchText);
    }
  }

  onSuburbSelectionChanged(suburb: string): void {
    const cityTownReference = this.getCityTownReferenceFromValue(suburb);
    this.referenceService.getBy(cityTownReference.key, 'townPostalCode')
      .pipe(take(1))
      .subscribe((reference: ReferenceDTO) => {
        if (reference) {
          this.addressFormGroup.patchValue({
            [Controls.PostalCode]: reference.value
          });
        } else {
          this.addressFormGroup.patchValue({
            [Controls.PostalCode]: ''
          });
        }
      });
  }

  private initForm(): void {
    this.addressFormGroup = this.formBuilder.group({
      [Controls.AddressTypeCode]: ['', Validators.required],
      [Controls.AddressLine1Number]: [''],
      [Controls.AddressLine1Name]: [''],
      [Controls.AddressLine2Number]: [''],
      [Controls.AddressLine2Name]: [''],
      [Controls.Suburb]: ['', Validators.required, suburbValidator((formValue) => this.searchSuburbs(formValue))],
      [Controls.CountryCode]: ['', Validators.required],
      [Controls.PostalCode]: [''],
      [Controls.POBoxNumber]: ['']
    });
  }

  private setFormValues(address: ApplicationAddressDTO): void {
    this.addressFormGroup.patchValue({
      [Controls.AddressTypeCode]: address.addressTypeCode,
      [Controls.AddressLine1Number]: address.addressLine1Number,
      [Controls.AddressLine1Name]: address.addressLine1Name,
      [Controls.AddressLine2Number]: address.addressLine2Number,
      [Controls.AddressLine2Name]: address.addressLine2Name,
      [Controls.Suburb]: `${address.suburb}`,
      [Controls.CountryCode]: address.countryCode,
      [Controls.PostalCode]: address.postalCode,
      [Controls.POBoxNumber]: this.removePoBoxPrefix(address.poBoxNumber)
    });
  }

  private searchSuburbs(searchText: string): Observable<any[]> {
    if (!searchText?.trim()) {
      return of([]);  // Return empty array for empty search
    }

    return this.referenceService.getAllReferencesByFieldNameAndValue('townCode', searchText).pipe(
      take(1),
      tap(results => {
        this.filteredSuburbs = results;  // Side effect: update filtered list
      }),
      catchError(error => {
        this.sharedService.openErrorDialog(error.message);
        return of([]);  // Return empty array on error
      })
    );
  }

  private removePoBoxPrefix(poBoxNumber: string): string {
    if (poBoxNumber) {
      return poBoxNumber.replace(/^P\.?O\.?\s?Box\s?/i, '');
    }
    return poBoxNumber;
  }

  private addPOBoxPrefix(poBoxNumber: string): string {
    if (poBoxNumber && !poBoxNumber.includes('P.O. Box')) {
      return `P.O. Box ${poBoxNumber}`;
    }
    return poBoxNumber;
  }

  private getCityTownReferenceFromValue(suburbValue) {
    if (suburbValue != null && this.filteredSuburbs != null && this.filteredSuburbs.length > 0) {
      return this.filteredSuburbs.find(x => x.value == suburbValue);
    } else {
      return null;
    }
  }
}
